import { useMemo, useState } from "react";
import {
  ActivityIndicator,
  FlatList,
  Pressable,
  Text,
  TextInput,
  View,
  type ListRenderItem,
} from "react-native";
import { useTranslation } from "react-i18next";
import {
  coerceLocale,
  rankByMatch,
  formatWeight,
  pickLocalizedText,
  type Exercise,
  DEFAULT_UNITS_PREFERENCE,
  type UnitsPreference,
} from "@heraion/shared";

import type { UiTheme } from "../theme";
import { useBreakpoint } from "../useBreakpoint";
import { makeStyles } from "./maxesStyles";
import { EditMaxSheet } from "./EditMaxSheet";
import { MaxEditForm } from "./MaxEditForm";
import { useMaxEditor } from "./useMaxEditor";
import type { ConfirmFn } from "../seams";
import type { MaxWriteFn } from "./useMaxEditor";
import type { MaxesStatus } from "./useMaxesData";
import { LoadErrorLine } from "../Progress/panelCards";
import { weightUnitLabels } from "../weightUnitLabels";
import { stateAria } from "../a11y";

// THE MAX management surface (list + edit sheet), one tree for mobile's
// Settings → Maxes screen and the web-rnw console. Presentational + self-
// contained: it owns search/filter/sort + the edit sheet (via useMaxEditor)
// and resolves localized names internally. The host supplies the catalog,
// the live MAX map, the apiFetch/forceRefresh data seam and a platform
// confirm dialog. Chrome (header / back / SafeAreaView) stays in the mount.
//
// Lists every P1RM-capable exercise (hasP1RM) PLUS any exercise that has a
// MAX recorded, so an entry bumped via the post-save pill stays visible
// even if its catalog row later drops the flag. Set MAXes sort first.

export type MaxesViewProps = {
  theme: UiTheme;
  /** Full catalog rows; null = still loading. */
  catalog: ReadonlyArray<Exercise> | null;
  /** Catalog load error code (e.g. `api_error_500`, `api_offline`); null =
   *  none. Shown as words, never the code (#397). */
  catalogError: string | null;
  /** Live { exerciseId → kg } map from useMaxesData / MaxesContext. */
  maxes: Map<string, number>;
  /** Whether `maxes` is known. The list waits while "loading": an empty map
   *  then is not an absence, so no row may say "Not set". Required for that
   *  reason. "failed" (offline, never loaded) shows the rows as unknown. */
  maxesStatus: MaxesStatus;
  /** With "failed": the read's failure code, said once above the rows by
   *  LoadErrorLine (offline neutral, a fault red, #397). Absent: rows only. */
  maxesError?: string | null;
  /** The athlete's own unit — the list reads in it, and it seeds the edit sheet
   *  (where the chip can still override it for one row).
   *  ⛔ REQUIRED on purpose, and that is the whole prevention: an optional prop
   *  with a "kg" fallback is a valid value of the type, so a mount that forgets
   *  it keeps typechecking while it corrupts a MAX. Why that is not
   *  hypothetical: useMaxEditor.ts, `UseMaxEditorDeps.units`.
   *  null = not known yet (web reads the profile after mounting): the list
   *  waits, so no row can open the sheet in a guessed unit. */
  units: UnitsPreference | null;
  /** Host MAX-write seam. ⛔ NOT the generic durableWrite: MAX has its own
   *  mobile queue and two queues on one resource cannot be ordered. */
  writeMax: MaxWriteFn;
  forceRefresh: () => Promise<unknown>;
  /** Platform clear-confirm dialog (mobile=Alert, web=window.confirm). */
  confirm: ConfirmFn;
  /** Safe-area insets (px) forwarded to the edit sheet's full-screen Modal;
   *  mobile feeds useSafeAreaInsets, web omits. */
  sheetInsets?: { top: number; bottom: number };
};

export function MaxesView({
  theme,
  catalog,
  catalogError,
  maxes,
  maxesStatus,
  maxesError = null,
  units,
  writeMax,
  forceRefresh,
  confirm,
  sheetInsets,
}: MaxesViewProps) {
  const { t, i18n } = useTranslation();
  const unitLabels = weightUnitLabels(t);
  const { wide } = useBreakpoint();
  const locale = coerceLocale(i18n.language);
  const styles = makeStyles(theme);
  // The list is the only door to openEdit and it stays shut while `units`
  // is null, so this default never seeds a sheet.
  const editor = useMaxEditor({
    writeMax,
    maxes,
    units: units ?? DEFAULT_UNITS_PREFERENCE,
    forceRefresh,
    confirm,
  });
  const ready = units !== null && maxesStatus !== "loading";

  const exName = (name: Exercise["name"]) => pickLocalizedText(name, locale);

  const [query, setQuery] = useState("");

  const rows = useMemo(() => {
    if (catalog === null) return null;
    const q = query.trim();
    // Every P1RM-capable exercise + any exercise that has a MAX recorded.
    const visibleSlugs = new Set<string>();
    for (const ex of catalog) {
      if (ex.hasP1RM) visibleSlugs.add(ex.slug);
    }
    for (const slug of maxes.keys()) visibleSlugs.add(slug);
    const ranked = rankByMatch(
      catalog.filter((ex) => visibleSlugs.has(ex.slug)),
      q,
      locale,
      (a, b) => a.name.en.localeCompare(b.name.en),
    );
    // Stable partition: set MAXes first, search order kept inside each group.
    return [
      ...ranked.filter((ex) => maxes.has(ex.slug)),
      ...ranked.filter((ex) => !maxes.has(ex.slug)),
    ];
  }, [catalog, maxes, query, locale]);

  const editingExercise = useMemo(
    () => catalog?.find((ex) => ex.slug === editor.editingSlug) ?? null,
    [catalog, editor.editingSlug],
  );

  const renderItem: ListRenderItem<Exercise> = ({ item }) => {
    const current = maxes.get(item.slug);
    // A failed read knows nothing about this row: a neutral en dash, never
    // «Not set» (MX-6) and no second error text (the line above says it).
    const unknown = current === undefined && maxesStatus === "failed";
    const value =
      current !== undefined
        ? formatWeight(
            current,
            units ?? DEFAULT_UNITS_PREFERENCE,
            i18n.language,
            unitLabels,
          )
        : unknown
          ? "\u2013"
          : t("mobile.maxes.notSet");
    // On the wide master-detail layout the row that fills the detail pane
    // gets a persistent highlight (the pressed flash isn't enough — the
    // selection outlives the tap). Narrow has no detail pane → no highlight.
    const isSelected = wide && editor.editingSlug === item.slug;
    return (
      <Pressable
        accessibilityRole="button"
        // ONE spoken element: «<exercise>, 140 kg» / «<exercise>, not set»,
        // not two Texts a reader may split (MX-7, #224).
        accessibilityLabel={
          unknown
            ? t("mobile.maxes.valueUnknown", { exercise: exName(item.name) })
            : `${exName(item.name)}, ${value}`
        }
        accessibilityState={{ selected: isSelected }}
        {...stateAria({ selected: isSelected }, "button")}
        style={({ pressed }) => [
          styles.row,
          isSelected && styles.rowSelected,
          pressed && styles.rowPressed,
        ]}
        onPress={() => editor.openEdit(item.slug)}
      >
        <Text style={styles.rowName} numberOfLines={1}>
          {exName(item.name)}
        </Text>
        <Text
          style={[
            styles.rowValue,
            current === undefined && styles.rowValueEmpty,
            unknown && styles.rowValueUnknown,
          ]}
        >
          {value}
        </Text>
      </Pressable>
    );
  };

  // Search + the result list (loading / error / empty / FlatList). Shared by
  // both layouts — it's the master pane on wide, the whole screen on narrow.
  const listContent = (
    <>
      <View style={styles.searchWrap}>
        <TextInput
          style={styles.search}
          placeholder={t("mobile.maxes.search")}
          placeholderTextColor={theme.palette.ink3}
          autoCorrect={false}
          autoCapitalize="none"
          value={query}
          onChangeText={setQuery}
        />
      </View>
      {catalogError !== null && rows === null ? (
        <View style={styles.center}>
          <LoadErrorLine theme={theme} error={catalogError} />
        </View>
      ) : rows === null || !ready ? (
        <View style={styles.center}>
          <ActivityIndicator color={theme.palette.ink2} />
        </View>
      ) : rows.length === 0 ? (
        <View style={styles.center}>
          <Text style={styles.emptyText}>{t("mobile.maxes.empty")}</Text>
        </View>
      ) : (
        <>
          {maxesStatus === "failed" && maxesError !== null ? (
            <View style={styles.loadErrorWrap}>
              <LoadErrorLine theme={theme} error={maxesError} />
            </View>
          ) : null}
          <FlatList
            data={rows ?? []}
            keyExtractor={(ex) => ex.slug}
            renderItem={renderItem}
            ItemSeparatorComponent={() => (
              <View
                style={[
                  styles.separator,
                  { backgroundColor: theme.palette.border },
                ]}
              />
            )}
            // ⛔ MX-9, LG-66's sibling hole on another surface. The search
            // TextInput sits right above this list (:165); the athlete types,
            // taps the first result, and RN's default "never" consumes that
            // touch to dismiss the keyboard instead of reaching the row's
            // onPress. Same cause as EffortMeter.tsx's fix, not testable here
            // either: RNTL invokes onPress directly and never models the
            // keyboard swallowing a touch.
            keyboardShouldPersistTaps="handled"
          />
        </>
      )}
    </>
  );

  // Wide (desktop console): master-detail. The list fills the left pane; the
  // selected exercise's edit form lives inline in the right pane (no modal).
  // Mobile is always narrow (phone width < the breakpoint) → it never takes
  // this branch, so the native screen stays byte-identical.
  if (wide) {
    return (
      <View style={styles.masterRow}>
        <View style={styles.listPane}>{listContent}</View>
        <View style={styles.detailPane}>
          {editingExercise ? (
            <>
              <Text style={styles.paneHeading} numberOfLines={1}>
                {exName(editingExercise.name)}
              </Text>
              <MaxEditForm
                key={editor.editingSlug}
                theme={theme}
                value={editor.editValue}
                onChangeValue={editor.setEditValue}
                unit={editor.editUnit}
                onToggleUnit={editor.toggleUnit}
                busy={editor.editBusy}
                error={editor.editError}
                canClear={editor.canClear}
                onClear={() => void editor.clear()}
                showSave
                onSave={() => void editor.save()}
                autoFocus
              />
            </>
          ) : (
            <View style={styles.empty}>
              <Text style={styles.emptyGlyph}>‹</Text>
              <Text style={styles.emptyTitle}>
                {t("mobile.maxes.selectPrompt", {
                  defaultValue: "Select an exercise to set its MAX.",
                })}
              </Text>
            </View>
          )}
        </View>
      </View>
    );
  }

  // Narrow (phone / narrow browser): list + the full-screen edit modal.
  return (
    <View style={styles.flex}>
      {listContent}

      <EditMaxSheet
        theme={theme}
        visible={editor.editingSlug !== null}
        exerciseName={editingExercise ? exName(editingExercise.name) : ""}
        value={editor.editValue}
        onChangeValue={editor.setEditValue}
        unit={editor.editUnit}
        onToggleUnit={editor.toggleUnit}
        busy={editor.editBusy}
        error={editor.editError}
        canClear={editor.canClear}
        onCancel={editor.closeEdit}
        onSave={() => void editor.save()}
        onClear={() => void editor.clear()}
        topInset={sheetInsets?.top}
        bottomInset={sheetInsets?.bottom}
      />
    </View>
  );
}
